import React from 'react';
import { Loader2 } from 'lucide-react';
import ToggleSwitch from './ToggleSwitch';
import { cn } from '../../utils/cn';

export interface EnableToggleProps {
  enabled: boolean;
  onChange: (next: boolean) => void | Promise<void>;
  loading?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  enabledLabel?: string;
  disabledLabel?: string;
  ariaLabel?: string;
  className?: string;
  stopPropagation?: boolean;
}

const labelSizes = {
  sm: 'text-[10px]',
  md: 'text-xs',
  lg: 'text-sm',
};

const spinnerSizes = {
  sm: 'h-3 w-3',
  md: 'h-3.5 w-3.5',
  lg: 'h-4 w-4',
};

/**
 * EnableToggle — the standard enable/disable control across the app.
 *
 * Renders a pill switch with an optional ENABLED / DISABLED text label,
 * a built-in loading spinner, and stopPropagation support for use inside
 * clickable parent rows.
 */
const EnableToggle: React.FC<EnableToggleProps> = ({
  enabled,
  onChange,
  loading = false,
  disabled = false,
  size = 'md',
  showLabel = false,
  enabledLabel = 'ENABLED',
  disabledLabel = 'DISABLED',
  ariaLabel,
  className,
  stopPropagation = false,
}) => {
  const isDisabled = disabled || loading;

  const handleChange = () => {
    if (isDisabled) return;
    onChange(!enabled);
  };

  const handleClick = (e: React.MouseEvent) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
  };

  const labelText = enabled ? enabledLabel : disabledLabel;
  const labelColor = enabled
    ? 'text-green-700 dark:text-green-400'
    : 'text-gray-500 dark:text-gray-400';

  return (
    <span
      className={cn('inline-flex items-center gap-2', className)}
      onClick={handleClick}
    >
      <span className="relative inline-flex items-center">
        <ToggleSwitch
          checked={enabled}
          onChange={handleChange}
          disabled={isDisabled}
          size={size}
          label={ariaLabel ?? labelText}
        />
        {loading && (
          <Loader2
            className={cn(
              'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 animate-spin text-white pointer-events-none',
              spinnerSizes[size],
            )}
          />
        )}
      </span>
      {showLabel && (
        <span
          className={cn(
            'font-semibold uppercase tracking-wider tabular-nums select-none',
            labelSizes[size],
            labelColor,
            isDisabled && 'opacity-60',
          )}
        >
          {labelText}
        </span>
      )}
    </span>
  );
};

export default EnableToggle;
